import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Activity, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApp } from "@/context/AppContext";
import { api, fmtDate } from "@/lib/api";

const DOT = { success: "bg-emerald-500", warning: "bg-amber-500", info: "bg-blue-500", error: "bg-red-600" };

const useAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = (bg) => api.get("/alerts", bg ? { headers: { "X-Background": "1" } } : undefined).then((r) => setAlerts(r.data)).catch(() => {});
    load(false);
    const t = setInterval(() => load(true), 10000);
    return () => clearInterval(t);
  }, []);
  return alerts;
};

const Alerts = () => {
  const alerts = useAlerts();
  const [seen, setSeen] = useState(() => localStorage.getItem("gamls_alerts_seen") || "");
  const unread = alerts.filter((a) => a.created_at > seen).length;
  const open = (o) => {
    if (o && alerts[0]) {
      localStorage.setItem("gamls_alerts_seen", alerts[0].created_at);
      setSeen(alerts[0].created_at);
    }
  };
  return (
    <>
      <div className="hidden xl:flex items-center gap-2 max-w-md truncate text-xs text-gray-600" data-testid="header-live-alert-ticker">
        <span className="flex items-center gap-1.5 rounded-full bg-[#FFF0F0] text-[#E60000] px-2 py-0.5 font-bold uppercase tracking-wider text-[10px]">
          <Activity className="w-3 h-3" /> Live
        </span>
        <span className="truncate">{alerts[0]?.message || "All systems normal"}</span>
      </div>
      <Popover onOpenChange={open}>
        <PopoverTrigger asChild>
          <button className="relative w-10 h-10 grid place-items-center rounded-md hover:bg-gray-100 transition-colors" data-testid="header-notifications-button">
            <Bell className="w-5 h-5 text-[#111]" />
            {unread > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E60000] text-white text-[10px] font-bold grid place-items-center" data-testid="header-notifications-count">{unread}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0" data-testid="header-alerts-panel">
          <div className="px-4 py-3 border-b font-heading font-bold text-sm">System Alerts</div>
          <div className="max-h-80 overflow-y-auto divide-y">
            {alerts.map((a) => (
              <div key={a.id} className="px-4 py-3 flex gap-3" data-testid="header-alert-item">
                <span className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${DOT[a.level]}`} />
                <div><div className="text-sm text-[#111]">{a.message}</div><div className="text-[11px] text-gray-500 mt-0.5">{fmtDate(a.created_at)}</div></div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export const Header = ({ collapsed, onToggle, onMobile }) => {
  const { user, role, logout } = useApp();
  const nav = useNavigate();
  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/");
  };
  return (
    <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB] flex items-center gap-3 px-4 sm:px-6">
      <button onClick={onMobile} className="lg:hidden w-10 h-10 grid place-items-center rounded-md hover:bg-gray-100" data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></button>
      <button onClick={onToggle} className="hidden lg:grid w-10 h-10 place-items-center rounded-md hover:bg-gray-100 transition-colors" data-testid="sidebar-toggle-button">
        {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
      </button>
      <Alerts />
      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        <div className="hidden sm:block text-right leading-tight" data-testid="header-user-profile-badge">
          <div className="text-sm font-semibold text-[#111]">{user?.name}</div>
          <div className="text-[11px] text-gray-500" data-testid="header-user-role">{role}</div>
        </div>
        <button onClick={() => nav("/change-password")} title="Change password" className="w-9 h-9 grid place-items-center rounded-md hover:bg-gray-100 transition-colors" data-testid="header-change-password-button"><KeyRound className="w-4 h-4" /></button>
        <button onClick={doLogout} className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#111] text-white text-sm font-medium hover:bg-[#E60000] transition-colors" data-testid="header-logout-button">
          <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
};
